import React, { useState } from 'react';
import { NavLink, withRouter, RouteComponentProps } from 'react-router-dom';

import { RouteChildType, RouteType } from '../../types/routes';
import '../../vendor/perfect-scrollbar.css';

import {
  StyledBox,
  StyledDrawer,
  Scrollbar,
  StyledList,
  Items,
  Brand,
  Category,
  CategoryText,
  CategoryIconLess,
  CategoryIconMore,
  Link,
  LinkText,
  LinkBadge,
  CategoryBadge,
  SidebarSection
} from './styles';

type SidebarCategoryPropsType = {
  name: string;
  icon: JSX.Element;
  classes?: string;
  isOpen?: boolean;
  isCollapsable?: boolean;
  badge?: string | number;
  activeClassName?: string;
  button: true;
  onClick?: () => void;
  to?: string;
  component?: typeof NavLink;
  exact?: boolean;
};

const SidebarCategory: React.FC<SidebarCategoryPropsType> = ({
  name,
  icon,
  classes,
  isOpen,
  isCollapsable,
  badge,
  ...rest
}) => {
  return (
    <Category {...rest}>
      {icon}
      <CategoryText>{name}</CategoryText>
      {isCollapsable ? isOpen ? <CategoryIconMore /> : <CategoryIconLess /> : null}
      {badge ? <CategoryBadge label={badge} /> : ''}
    </Category>
  );
};

type SidebarLinkPropsType = {
  name: string;
  to: string;
  badge?: string | number;
  icon?: JSX.Element;
};

type SidebarPropsType = {
  classes?: string;
  staticContext: string;
  location: {
    pathname: string;
  };
  PaperProps: {
    style: {
      width: number;
    };
  };
  variant?: 'permanent' | 'persistent' | 'temporary';
  open?: boolean;
  onClose?: () => void;
  routes: RouteType[];
};

const SidebarLink: React.FC<SidebarLinkPropsType> = ({ name, to, badge, icon }) => {
  return (
    <Link button dense component={NavLink} exact to={to} activeClassName="active">
      <LinkText>{name}</LinkText>
      {badge ? <LinkBadge label={badge} /> : ''}
    </Link>
  );
};

const Sidebar: React.FC<RouteComponentProps & SidebarPropsType> = ({
  classes,
  staticContext,
  location,
  routes,
  ...rest
}) => {
  type tplotOptions = {
    [key: number]: boolean;
  };

  const initOpenRoutes = (): tplotOptions => {
    /* Open collapse element that matches current url */
    const pathName = location.pathname;

    let _routes = {};

    routes.forEach((route: any, index) => {
      const isActive = pathName.indexOf(route.path) === 0;
      const isOpen = route.open;
      const isHome = route.containsHome && pathName === '/';

      _routes = Object.assign({}, _routes, {
        [index]: isActive || isOpen || isHome
      });
    });

    return _routes;
  };

  const [openRoutes, setOpenRoutes] = useState(() => initOpenRoutes());

  const toggle = (index: number) => {
    // Collapse all elements
    Object.keys(openRoutes).forEach(
      item =>
        openRoutes[index] || setOpenRoutes(openRoutes => Object.assign({}, openRoutes, { [item]: false }))
    );

    // Toggle selected element
    setOpenRoutes(openRoutes => Object.assign({}, openRoutes, { [index]: !openRoutes[index] }));
  };

  return (
    <StyledDrawer variant="permanent" {...rest}>
      <Brand component={NavLink} to="/" button>
        <StyledBox ml={1}>Backoffice</StyledBox>
      </Brand>
      <Scrollbar>
        <StyledList disablePadding>
          <Items>
            {routes.map((category: any, index) => (
              <React.Fragment key={index}>
                {category.header ? <SidebarSection>{category.header}</SidebarSection> : null}
                {category.children && category.icon ? (
                  <React.Fragment key={index}>
                    <SidebarCategory
                      name={category.id}
                      icon={category.icon}
                      button={true}
                      onClick={() => toggle(index)}
                    />
                    {category.children.map((route: RouteChildType, index: number) => (
                      <SidebarLink
                        key={index}
                        name={route.name}
                        to={route.path}
                        icon={route.icon}
                        badge={route.badge}
                      />
                    ))}
                  </React.Fragment>
                ) : category.icon ? (
                  <SidebarCategory
                    name={category.id}
                    to={category.path}
                    activeClassName="active"
                    component={NavLink}
                    icon={category.icon}
                    exact
                    button
                    badge={category.badge}
                  />
                ) : null}
              </React.Fragment>
            ))}
          </Items>
        </StyledList>
      </Scrollbar>
    </StyledDrawer>
  );
};

export default withRouter(Sidebar);
